import { useEffect, useMemo, useState } from 'react';
import { client } from '../../hooks/Client';
import MultipleMomentLineChart from '../Visualisations/moment/MultipleMomentLineChart';
import MultipleMomentBarChart from '../Visualisations/moment/MultipleMomentBarChart';
import MultipleMomentPieChart from '../Visualisations/moment/MultipleMomentPieChart';
import MultipleDevelopmentLineChart from '../Visualisations/development/MultipleDevelopmentLineChart';
import MultipleDevelopmentBarChart from '../Visualisations/development/MultipleDevelopmentBarChart';
import MultipleDevelopmentPieChart from '../Visualisations/development/MultipleDevelopmentPieChart';
import defaultDates from '../../utils/defaultDates';
import useHierarchyData from '../../hooks/useHierarchyData';
import toMillis from '../../helpers/dataHelpers/matrixDataHelpers/toMillis';
import spinner from '../../images/spinner-ripple.svg';
import { resolveColor } from '../../helpers/visualisationDefaults';
import { OTHER_BUCKET_COLOR } from '../../helpers/chartPalette';
import useChartColors from '../../hooks/useChartColors';
import notDeleted from '../../helpers/notDeleted';
import { groupMultipleByMoment } from '../../helpers/dataHelpers/countMultipleOptions';
import momentTitlesForRecords from '../../helpers/dataHelpers/momentTitlesForRecords';
import scaleOptionColors from '../../helpers/dataHelpers/scaleOptionColors';
import { multipleChoiceTable } from '../../helpers/dataHelpers/chartDataTable';
import ChartDataTable from './ChartDataTable';
import { definitionCollection } from '../../firebase/publicDefinitions';

// AI-CONTEXT: See docs/features/api-data-fetching.md

// AI-CONTEXT: See docs/features/api-data-fetching.md

// ───────────── session caches ─────────────
const fieldCache   = new Map();
const momentsCache = new Map();
const optionsCache = new Map();

// ───────────── helpers ─────────────
const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const shallowEqualArray = (a, b) => {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const ai = a[i], bi = b[i];
    if (ai === bi) continue;
    const aKeys = Object.keys(ai || {}), bKeys = Object.keys(bi || {});
    if (aKeys.length !== bKeys.length) return false;
    for (const k of aKeys) if (ai[k] !== bi[k]) return false;
  }
  return true;
};

const getFieldDoc = async (field) => {
  if (fieldCache.has(field)) return fieldCache.get(field);
  const snap = await definitionCollection('QuestionnaireFields')
    .where('CompagnyID', '==', client)
    .where('ID', '==', field)
    .limit(1)
    .get();
  const doc = snap.docs[0]?.data() || {};
  fieldCache.set(field, doc);
  return doc;
};

const getMeasureMoments = async (researchIds) => {
  const key = JSON.stringify([...researchIds].sort());
  if (momentsCache.has(key)) return momentsCache.get(key);
  if (!researchIds.length) {
    momentsCache.set(key, []);
    return [];
  }
  const chunks = chunk(researchIds, 10);
  const snaps = await Promise.all(chunks.map(c =>
    definitionCollection('MeasureMoments')
      .where('CompagnyID', '==', client)
      .where('ResearchID', 'in', c)
      .orderBy('Position', 'asc')
      .get()
  ));
  const all = snaps.flatMap(s => s.docs.map(d => d.data()));
  const seen = new Set();
  const out = [];
  for (const m of all) {
    if (m?.Deleted) continue; // soft-deleted (ADR-008)
    const id = m?.ID;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(m);
  }
  momentsCache.set(key, out);
  return out;
};

const getOptions = async (field) => {
  if (optionsCache.has(field)) return optionsCache.get(field);
  const snap = await definitionCollection('MultipleQuestionOptions')
    .where('CompagnyID', '==', client)
    .where('Field', '==', field)
    .orderBy('Position', 'asc')
    .get();

  const fixedOptions = [];
  snap.docs.filter(d => notDeleted(d.data())).forEach(d => {
    const row = d.data();
    const name = row?.Option || '';
    const isOther =
      row?.OptionType === 'other' ||
      name.toLowerCase() === 'other' ||
      name.toLowerCase() === 'overig';
    if (!isOther) {
      fixedOptions.push({ id: row?.ID || name, name });
    }
  });

  const res = { fixedOptions };
  optionsCache.set(field, res);
  return res;
};

/**
 * @param {object} props
 * @param {string} props.field
 * @param {string|string[]} props.researchId
 * @param {string} [props.graphType]
 * @param {string} [props.startDate]
 * @param {string} [props.endDate]
 * @param {string} [props.personaId]
 * @param {boolean} [props.filterByResearchId] - whether to filter synced data by researchId (only include records from those researchIds)
 * @param {Array<string>} [props.colorPalette]
 * @param {Object<string, string>} [props.colorOverrides] - map of option ID to color, used to override default palette colors
 * @param {'positive-first'|'negative-first'|''} [props.scaleColors] - Antwoorden als schaal kleuren (rood ↔ teal) in plaats van categorisch
 * @returns 
 */
const MultipleData = ({
  field,
  researchId,
  startDate,
  endDate,
  graphType,
  graphColor,
  personaId,
  filterByResearchId,
  colorPalette,
  colorOverrides,
  scaleColors,
  onTotalResponsesChange,
  onCompareData,
}) => {
  const { palette, overrides, inLiveReport } = useChartColors({ colorPalette, colorOverrides });

  const [meta, setMeta] = useState({
    scaleEnd: 0,
    fixedOptions: [],
    id2pos: {},
    pos2title: {},
    metaLoading: true,
  });

  const { defaultStartDate: dsRaw, defaultEndDate: deRaw } = useMemo(() => defaultDates(), []);
  const dsMs = useMemo(() => toMillis(dsRaw) ?? 0, [dsRaw]);
  const deMs = useMemo(() => toMillis(deRaw) ?? 8640000000000000, [deRaw]);

  const researchIds = useMemo(
    () => (Array.isArray(researchId) ? researchId : [researchId]).filter(Boolean),
    [researchId]
  );

  const startMs = useMemo(() => toMillis(startDate) ?? dsMs, [startDate, dsMs]);
  const endMs   = useMemo(() => toMillis(endDate)   ?? deMs, [endDate, deMs]);

  // Fetch metadata (field doc, measure moments, options)
  useEffect(() => {
    if (!field) return;
    let cancelled = false;

    Promise.all([
      getFieldDoc(field),
      getMeasureMoments(researchIds),
      getOptions(field),
    ]).then(([fieldDoc, mmList, options]) => {
      if (cancelled) return;

      const id2pos = {};
      const pos2title = {};
      (mmList || []).forEach(m => {
        const pos = typeof m?.Position === 'number' ? m.Position : parseInt(m?.Position, 10);
        if (!Number.isNaN(pos)) {
          id2pos[m.ID] = pos;
          if (m?.Title) pos2title[pos] = m.Title;
        }
      });

      setMeta({
        scaleEnd: fieldDoc?.ReachEnd ?? 0,
        fixedOptions: options.fixedOptions,
        id2pos,
        pos2title,
        metaLoading: false,
      });
    });

    return () => { cancelled = true; };
  }, [field, JSON.stringify(researchIds)]);

  // Fetch records via Cloud Function
  const { records, hierarchy: projectHierarchy, totalRespondents, loading: recordsLoading, error } = useHierarchyData({
    syncType: 'Question',
    id: field,
    startDate: startMs,
    endDate: endMs,
    researchIds,
    personaId,
    filterByResearchId,
  });

  // Process records into chart data
  const processedData = useMemo(() => {
    if (meta.metaLoading || recordsLoading || !records.length) {
      return { data: [], otherInputs: [], totalResponses: 0 };
    }

    const { fixedOptions, id2pos, pos2title } = meta;

    // Alle opties per meetmoment, ook die met 0 antwoorden; antwoorden zonder
    // bekend meetmoment in één bak in plaats van weg.
    // Meerdere onderzoeken → alleen posities; zie momentTitlesForRecords.
    const grouped = groupMultipleByMoment(records, fixedOptions, id2pos, momentTitlesForRecords(pos2title, records));
    const otherInputs = grouped[0]?.others || [];

    return { data: grouped, otherInputs, totalResponses: totalRespondents };
  }, [records, meta, recordsLoading, totalRespondents]);

  const { data, totalResponses } = processedData;
  const loading = meta.metaLoading || recordsLoading;

  useEffect(() => {
    onTotalResponsesChange?.(totalResponses);
  }, [totalResponses, onTotalResponsesChange]);

  // Schaalkleuren gaan vóór de categorische, de per-kaart gekozen kleuren
  // ('overrides') daarbovenop: een bewuste keuze van de maker blijft staan.
  const scaleColorMap = useMemo(
    () => scaleOptionColors(meta.fixedOptions, scaleColors),
    [meta.fixedOptions, scaleColors]
  );

  // Inject colors at render time
  const coloredData = useMemo(() => {
    return data.map(moment => ({
      ...moment,
      responses: moment.responses.map(resp => ({
        ...resp,
        color: resp.isOther
          ? (overrides?.['__other__'] || OTHER_BUCKET_COLOR)
          : (overrides?.[resp.id] || scaleColorMap?.[resp.id] || resolveColor(resp.id, resp.optIndex, palette, overrides)),
      })),
    }));
  }, [data, palette, overrides, scaleColorMap]);

  // Compare-two-periods (docs/features/compare-two-periods.md): report this
  // period's single-moment option counts — exactly what the moment bar chart
  // renders — so a wrapping compare view can show grouped bars (period A vs B
  // per option) without recomputing (no dual-calculation divergence).
  useEffect(() => {
    if (!onCompareData) return;
    const single = coloredData.length === 1;
    const series = single
      ? coloredData[0].responses.map(r => ({ name: r.name, value: r.count }))
      : [];
    onCompareData({ kind: 'grouped', eligible: single && series.length > 0, series });
  }, [coloredData, onCompareData]);

  if (loading) return <img src={spinner} alt="loading" />;
  // Een mislukte read is geen lege vraag: zonder deze tak stond er 'Nog geen
  // gegevens' terwijl de antwoorden er waren (docs/features/public-livereport-data.md).
  // AI-CONTEXT: See docs/pitfalls.md#api-fallback-collapses-failure-into-empty
  if (error) return <p className="graph-data-error">De gegevens konden niet worden geladen.</p>;

  const isSingle = coloredData.length < 2;
  const mode = isSingle ? 'single' : 'multiple';

  const graphComponents = {
    single: {
      line: <MultipleMomentLineChart data={coloredData} totalResponses={totalResponses} graphColor={graphColor} />,
      bar:  <MultipleMomentBarChart   data={coloredData} totalResponses={totalResponses} graphColor={graphColor} />,
      pie:  <MultipleMomentPieChart   data={coloredData} totalResponses={totalResponses} graphColor={graphColor} />,
    },
    multiple: {
      line: <MultipleDevelopmentLineChart data={coloredData} totalResponses={totalResponses} graphColor={graphColor} />,
      bar:  <MultipleDevelopmentBarChart  data={coloredData} totalResponses={totalResponses} graphColor={graphColor} />,
      pie:  <MultipleDevelopmentPieChart  data={coloredData} totalResponses={totalResponses} graphColor={graphColor} />,
    },
  };

  const chart = graphComponents[mode][graphType || 'bar'] || null;

  // De cijfers onder de grafiek, alleen op het PDF-vel (ChartDataTable poort zelf).
  const table = chart && <ChartDataTable table={multipleChoiceTable(coloredData)} />;

  // In een live rapportage staat de kop met 'Details' (waar het aantal
  // invullingen in staat) niet; daar zet de grafiek zelf n erboven, direct
  // onder de vraag, net als bij de open antwoorden en de cirkels per stelling.
  if (!inLiveReport || !chart || totalResponses === 0) return <>{chart}{table}</>;

  return (
    <>
      <p className="chart-sample-size">n = {totalResponses}</p>
      {chart}
      {table}
    </>
  );
};

export default MultipleData;
